(function () {
  const productQueryKeys = [
    'show_products',
    'view_all',
    'products',
  ];
  const filterQueryPattern = /(?:^|[/?&])(?:Year|Make|Model|Submodel|custom_[^/=]+)/i;

  function hasFilterQuery() {
    return filterQueryPattern.test(window.location.search.replace(/^\?/, ''));
  }

  function shouldShowProducts() {
    const params = new URLSearchParams(window.location.search);

    return hasFilterQuery() || productQueryKeys.some((key) => {
      const value = params.get(key);
      return value === '1' || value === 'true' || value === 'yes';
    });
  }

  function syncProductVisibility() {
    const visible = shouldShowProducts();
    document.documentElement.classList.toggle('parity-moto-hub-products-visible', visible);

    const hub = document.querySelector('[data-parity-moto-hub]');
    const productSectionSuffix = hub?.dataset.parityMotoProductSectionSuffix || 'motorcycle-products';
    const hubSection = hub && hub.closest('[id^="shopify-section-"]');
    const productHeader = hubSection && hubSection.querySelector('[data-parity-moto-product-header]');
    const sectionParent = hubSection && hubSection.parentNode;

    if (productHeader) {
      productHeader.hidden = !visible;
    }

    document.querySelectorAll(`[id$="${productSectionSuffix}"]`).forEach((section) => {
      section.toggleAttribute('aria-hidden', !visible);

      if (!hubSection || !sectionParent) return;

      if (visible) {
        if (productHeader) {
          sectionParent.insertBefore(productHeader, hubSection);
        }

        sectionParent.insertBefore(section, hubSection);
        return;
      }

      if (productHeader && productHeader.parentNode !== hubSection) {
        hubSection.insertBefore(productHeader, hubSection.firstChild);
      }

      if (hubSection.nextSibling !== section) {
        sectionParent.insertBefore(section, hubSection.nextSibling);
      }
    });
  }

  function escapeHtml(value) {
    const replacements = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };

    return String(value || '').replace(/[&<>"']/g, (character) => replacements[character]);
  }

  function imageUrlWithWidth(src, width) {
    if (!src) return '';

    try {
      const url = new URL(src, window.location.origin);
      if (!url.searchParams.has('width')) {
        url.searchParams.set('width', String(width));
      }
      return url.toString();
    } catch (_error) {
      return src;
    }
  }

  function variantPriceCents(variant) {
    const price = Number(variant?.price);
    if (!Number.isFinite(price)) return null;
    return Math.round(price * 100);
  }

  function formatMoney(cents) {
    if (!Number.isFinite(cents)) return '';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const moneyFormat = window.theme?.moneyFormat || window.moneyFormat || '${{amount}}';
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  }

  function productPriceLabel(product) {
    const prices = (product.variants || [])
      .map(variantPriceCents)
      .filter((price) => Number.isFinite(price));

    if (!prices.length) return '';

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const prefix = minPrice !== maxPrice ? 'From ' : '';

    return `${prefix}${formatMoney(minPrice)}`;
  }

  function productImageSrc(product) {
    const firstImage = product.images?.[0] || product.featured_image;
    return typeof firstImage === 'string' ? firstImage : firstImage?.src || '';
  }

  function renderBestSellerCard(product) {
    const title = product.title || '';
    const productUrl = product.handle ? `/products/${product.handle}` : '#';
    const imageSrc = imageUrlWithWidth(productImageSrc(product), 360);
    const priceLabel = productPriceLabel(product);
    const mediaMarkup = imageSrc
      ? `<img src="${escapeHtml(imageSrc)}" alt="${escapeHtml(title)}" loading="lazy">`
      : '<span class="parity-best-sellers__placeholder" aria-hidden="true"></span>';
    const priceMarkup = priceLabel
      ? `<span class="parity-best-sellers__price">${escapeHtml(priceLabel)}</span>`
      : '';

    return `
      <li class="parity-best-sellers__item">
        <a class="parity-best-sellers__card" href="${escapeHtml(productUrl)}">
          <span class="parity-best-sellers__media">${mediaMarkup}</span>
          <span class="parity-best-sellers__name">${escapeHtml(title)}</span>
          ${priceMarkup}
        </a>
      </li>
    `;
  }

  function syncBestSellerControls(section) {
    const viewport = section.querySelector('[data-parity-best-sellers-carousel]');
    const prev = section.querySelector('[data-parity-best-sellers-prev]');
    const next = section.querySelector('[data-parity-best-sellers-next]');

    if (!viewport || !prev || !next) return;

    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const hasOverflow = maxScrollLeft > 4;

    prev.disabled = !hasOverflow || viewport.scrollLeft <= 4;
    next.disabled = !hasOverflow || viewport.scrollLeft >= maxScrollLeft - 4;
  }

  function bindBestSellerCarousel(section) {
    if (section.dataset.parityBestSellersBound === 'true') {
      syncBestSellerControls(section);
      return;
    }

    const viewport = section.querySelector('[data-parity-best-sellers-carousel]');
    const prev = section.querySelector('[data-parity-best-sellers-prev]');
    const next = section.querySelector('[data-parity-best-sellers-next]');

    if (!viewport || !prev || !next) return;

    const scrollByPage = (direction) => {
      viewport.scrollBy({
        left: direction * Math.max(160, viewport.clientWidth * 0.85),
        behavior: 'smooth',
      });
    };

    prev.addEventListener('click', () => scrollByPage(-1));
    next.addEventListener('click', () => scrollByPage(1));
    viewport.addEventListener('scroll', () => syncBestSellerControls(section), { passive: true });
    window.addEventListener('resize', () => syncBestSellerControls(section));

    if (window.ResizeObserver) {
      new ResizeObserver(() => syncBestSellerControls(section)).observe(viewport);
    }

    section.dataset.parityBestSellersBound = 'true';
    syncBestSellerControls(section);
  }

  async function hydrateBestSellers(section) {
    if (section.dataset.parityBestSellersHydrated === 'true') return;
    if (typeof fetch !== 'function') return;

    const productsUrl = section.dataset.parityBestSellersUrl;
    const track = section.querySelector('[data-parity-best-sellers-track]');

    if (!productsUrl || !track) return;

    section.dataset.parityBestSellersHydrated = 'true';

    try {
      const response = await fetch(productsUrl, {
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) return;

      const payload = await response.json();
      const products = payload.products || [];

      if (!products.length) return;

      track.innerHTML = products.map(renderBestSellerCard).join('');
      syncBestSellerControls(section);
    } catch (_error) {
      // Keep the Liquid-rendered fallback cards.
    }
  }

  function initBestSellers() {
    document.querySelectorAll('[data-parity-best-sellers]').forEach((section) => {
      bindBestSellerCarousel(section);
      hydrateBestSellers(section);
    });
  }

  function init() {
    if (!document.querySelector('[data-parity-moto-hub]')) return;

    syncProductVisibility();
    initBestSellers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
